import { Capacitor } from '@capacitor/core'

/**
 * Enregistrement du service worker — la partie « installable » de la PWA.
 *
 * Le fichier lui-même vit dans `public/sw.js` (copié tel quel dans `dist/` par
 * Vite, donc servi à la racine du déploiement : c'est ce qui lui donne une
 * portée sur toute l'app). Stratégies de cache : voir ses commentaires.
 *
 * Trois refus, et ils comptent :
 *
 * 1. **natif** — en webview Capacitor, `dist/` est déjà embarqué dans le
 *    bundle. Un SW n'apporterait rien et servirait, après une mise à jour de
 *    l'app, l'ancienne version depuis son cache. Android sert l'app en
 *    `https://localhost`, donc en contexte sécurisé : sans ce test, le SW
 *    s'installerait bel et bien.
 * 2. **dev** — le SW masquerait le HMR. Mieux : on désinscrit ce qui traîne,
 *    parce qu'un `npm run preview` (build de prod) tourne sur le même hôte que
 *    Vite et laisse un SW derrière lui qui, sinon, continuerait à servir sa
 *    coquille en cache sur `localhost:5173`.
 * 3. **contexte non sécurisé** — `http://` sur IP LAN (le live reload). Le
 *    `register()` échouerait ; on n'essaie même pas, comme `useAuth` pour
 *    WebCrypto.
 *
 * Pas de `skipWaiting()` : une nouvelle version s'active quand les onglets se
 * ferment, sans recharger la page sous les doigts de l'utilisateur. Ça ne fige
 * rien, la navigation étant servie réseau d'abord — un nouveau déploiement est
 * pris au prochain chargement, SW à jour ou non.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  if (Capacitor.isNativePlatform()) return

  if (import.meta.env.DEV) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
    return
  }

  if (!window.isSecureContext) return

  try {
    const registration = await navigator.serviceWorker.register(
      `${import.meta.env.BASE_URL}sw.js`,
      { scope: import.meta.env.BASE_URL },
    )

    // une app installée peut rester ouverte des jours : on demande au navigateur
    // de revérifier le SW quand l'app revient au premier plan
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void registration.update()
    })
  } catch (error) {
    // un SW indisponible (hôte sans HTTPS, navigation privée Firefox…) ne doit
    // jamais empêcher l'app de tourner : elle marche sans, simplement en ligne
    console.warn('[pwa] service worker non enregistré', error)
  }
}
